import { useEffect, useRef, useState } from "react";
import { ChevronDown, CheckCircle2, Search } from "lucide-react";

/**
 * Dual-mode contextual selector.
 *
 * Compact pill (default):   type filter, small inline triggers
 * Full-width card:          church selector, entity pickers
 *   - auto-shows search input when items.length >= 5
 */
export default function InlinePicker({
  items = [],
  value,
  onChange,
  className = "",
  // Full-width card mode
  fullWidth = false,
  icon = null,
  triggerLabel = null,
  placeholder = "Seleccionar…",
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const active     = items.find((i) => String(i.value) === String(value)) || null;
  const showSearch = fullWidth && items.length >= 5;

  // Compact: highlight trigger when a non-default filter is active
  const isFiltered = !fullWidth && active && items[0] && String(active.value) !== String(items[0].value);

  const displayed = query
    ? items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()))
    : items;

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close, true);
    document.addEventListener("touchstart", close, true);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("touchstart", close, true);
    };
  }, [open]);

  const pick = (v) => { onChange?.(v); setOpen(false); };

  // ── Shared dropdown list ─────────────────────────────────────────────
  const DropdownList = ({ rowClass = "", textClass = "" }) =>
    displayed.length === 0 ? (
      <p className="text-[13px] text-slate-400 dark:text-slate-500 text-center py-4">Sin resultados</p>
    ) : displayed.map((item) => {
      const sel = String(item.value) === String(value);
      return (
        <button
          key={item.value}
          onClick={() => pick(item.value)}
          className={`w-full flex items-center justify-between gap-3 text-left transition-colors duration-150 ${rowClass} ${
            sel
              ? "bg-brand-light dark:bg-brand/15"
              : "hover:bg-slate-50 dark:hover:bg-slate-700/50 active:bg-slate-100 dark:active:bg-slate-700"
          }`}
        >
          <span className={`${textClass} font-semibold truncate ${sel ? "text-brand dark:text-blue-300" : "text-slate-700 dark:text-slate-200"}`}>
            {item.label}
          </span>
          {sel && <CheckCircle2 size={15} className="text-brand dark:text-blue-400 shrink-0" strokeWidth={2} />}
        </button>
      );
    });

  // ── Compact pill ─────────────────────────────────────────────────────
  if (!fullWidth) {
    return (
      <div ref={ref} className={`relative inline-block ${className}`}>
        <button
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center gap-1.5 h-9 px-3.5 rounded-full border font-semibold text-[12px]
            transition-all duration-200 ease-out active:scale-[0.97] ${
            open
              ? "bg-brand border-brand text-white shadow-[0_2px_8px_rgba(27,79,156,0.25)]"
              : isFiltered
              ? "bg-brand-light dark:bg-brand/15 border-brand/30 dark:border-brand/40 text-brand dark:text-blue-300"
              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600"
          }`}
        >
          {active?.label || placeholder}
          <ChevronDown
            size={12}
            strokeWidth={2.5}
            className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute left-0 top-[calc(100%+6px)] z-[70] min-w-[168px] bg-white dark:bg-slate-800 rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.16)] border border-slate-200/60 dark:border-slate-700/60 overflow-hidden origin-top-left animate-in fade-in zoom-in-95 duration-150">
            <div className="py-2 max-h-72 overflow-y-auto">
              <DropdownList rowClass="px-4 py-2.5" textClass="text-[13px]" />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Full-width card ──────────────────────────────────────────────────
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 bg-white dark:bg-slate-800 rounded-[20px] px-4 py-3.5 border shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-all duration-200 ease-out active:scale-[0.98] ${
          open
            ? "border-brand/40 dark:border-brand/40 shadow-[0_4px_16px_rgba(27,79,156,0.12)]"
            : "border-slate-200/50 dark:border-slate-700/50"
        }`}
      >
        {icon && (
          <div className="h-9 w-9 rounded-[12px] bg-brand-light dark:bg-brand/20 flex items-center justify-center shrink-0 text-brand dark:text-blue-400">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0 text-left">
          {triggerLabel && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium uppercase tracking-wide">
              {triggerLabel}
            </p>
          )}
          <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 truncate">
            {active?.label || placeholder}
          </p>
        </div>
        <ChevronDown
          size={16}
          className={`text-slate-400 dark:text-slate-500 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[70] bg-white dark:bg-slate-800 rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.16)] border border-slate-200/60 dark:border-slate-700/60 overflow-hidden origin-top animate-in fade-in zoom-in-95 duration-150">
          {showSearch && (
            <div className="px-3 pt-3 pb-1.5 border-b border-slate-100 dark:border-slate-700/60">
              <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/50 rounded-[12px] px-3 h-9">
                <Search size={13} className="text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar…"
                  className="flex-1 bg-transparent text-[13px] text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none"
                />
              </div>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1.5">
            <DropdownList rowClass="px-4 py-3" textClass="text-[14px]" />
          </div>
        </div>
      )}
    </div>
  );
}
